import React, { useState, useEffect } from 'react';
import { useAuthV2 } from '../../context/auth-v2/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Form, Input, TextArea, Select, Alert } from '../../components/ui';
import { createClient } from '../../services/supabase';
import { MapPinIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { LocationService } from '../../services/location.service';
import { useDevMode } from '../../utils/environment';
import axios from 'axios';

interface EventFormData {
  title: string;
  description: string;
  date: string;
  start_time: string;
  end_time: string;
  location: string;
  parish: string;
  max_attendees: number;
  event_type: string;
  image_url?: string;
}

interface AddressSuggestion {
  place_id: string;
  description: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
}

const BERMUDA_PARISHES = [
  'Devonshire',
  'Hamilton',
  'Paget',
  'Pembroke',
  'St. George\'s',
  'Sandys',
  'Smith\'s',
  'Southampton',
  'Warwick'
];

const EVENT_TYPES = [
  'Food Distribution',
  'Clothing Drive',
  'Community Outreach',
  'Fundraising',
  'Volunteer Training',
  'Other'
];

const AddEvent: React.FC = () => {
  const { user } = useAuthV2();
  const navigate = useNavigate();
  const isDevEnvironment = useDevMode();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [locationSuggestions, setLocationSuggestions] = useState<AddressSuggestion[]>([]);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [formData, setFormData] = useState<EventFormData>({
    title: '',
    description: '',
    date: '',
    start_time: '',
    end_time: '',
    location: '',
    parish: '',
    max_attendees: 10,
    event_type: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleParishChange = (value: string) => {
    setFormData(prev => ({ ...prev, parish: value }));
  };

  const handleEventTypeChange = (value: string) => {
    setFormData(prev => ({ ...prev, event_type: value }));
  };

  // Handle location search when user types in location field
  useEffect(() => {
    const searchTimeout = setTimeout(async () => {
      if (searchQuery.length >= 3) {
        try {
          const suggestions = await LocationService.getAddressSuggestions(searchQuery);
          setLocationSuggestions(suggestions);
          setShowLocationSuggestions(suggestions.length > 0);
        } catch (error) {
          console.error('Error fetching location suggestions:', error);
        }
      } else {
        setLocationSuggestions([]);
        setShowLocationSuggestions(false);
      }
    }, 500);

    return () => clearTimeout(searchTimeout);
  }, [searchQuery]);

  const handleLocationInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    setFormData(prev => ({ ...prev, location: query }));
    if (query !== formData.location) {
      setSelectedLocationId('');
    }
  };

  const selectLocation = (suggestion: AddressSuggestion) => {
    setFormData(prev => ({ ...prev, location: suggestion.description }));
    setSearchQuery(suggestion.description);
    setSelectedLocationId(suggestion.place_id);
    setShowLocationSuggestions(false);
  };
  
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError("Image size should not exceed 5MB");
      return;
    }
    
    // Check file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      setError("Please select a valid image file (JPG, PNG, or GIF)");
      return;
    }
    
    setSelectedImage(file);
    
    // Create a preview URL
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };
  
  const uploadImage = async (): Promise<string | null> => {
    if (!selectedImage) return null;
    
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedImage);
      
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/charity-buckets/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${await (await createClient()).auth.getSession().then((res: any) => res.data.session?.access_token)}`
          }
        }
      );
      
      setUploadingImage(false);
      return response.data.public_url;
    } catch (error) {
      console.error('Error uploading image:', error);
      setUploadingImage(false);
      setError('Failed to upload image. Please try again.');
      return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Upload image first if selected
      let imageUrl = null;
      if (selectedImage) {
        imageUrl = await uploadImage();
        if (!imageUrl) {
          setLoading(false);
          return; // Stop if image upload failed
        }
      }
      
      const eventDate = formData.date;
      
      // Prepare location data
      let locationData: any = { location: formData.location };
      
      if (selectedLocationId) {
        locationData.place_id = selectedLocationId;
      } else if (formData.location) {
        try {
          // If no specific place_id, try to geocode the address
          const geocodeResult = await LocationService.geocodeAddress(formData.location);
          if (geocodeResult) {
            locationData = {
              location: geocodeResult.formatted_address,
              lat: geocodeResult.lat,
              lng: geocodeResult.lng
            };
          }
        } catch (locError) {
          console.error('Geocoding error (non-fatal):', locError);
          // Continue with user-entered location
        }
      }
      
      // Create event in Supabase
      const supabase = await createClient();
      const { error: supabaseError } = await supabase
        .from('events')
        .insert([
          {
            event_name: formData.title,
            description: formData.description,
            event_date: eventDate,
            start_time: formData.start_time,
            end_time: formData.end_time,
            location: JSON.stringify(locationData),
            parish: formData.parish,
            max_attendees: formData.max_attendees,
            event_type: formData.event_type,
            created_by: user?.id,
            image_url: imageUrl
          }
        ])
        .select();

      if (supabaseError) throw supabaseError;

      setSuccess('Event created successfully');
      setFormData({
        title: '',
        description: '',
        date: '',
        start_time: '',
        end_time: '',
        location: '',
        parish: '',
        max_attendees: 10,
        event_type: ''
      });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="space-y-6">
          <div className="flex items-center">
            <button
              type="button"
              onClick={() => navigate(isDevEnvironment ? '/dev/charity/dashboard' : '/charity/dashboard')}
              className="mr-4 text-gray-500 hover:text-navy-blue"
            >
              
            </button>
            <h1 className="text-xl font-medium text-navy-blue">Add Event</h1>
          </div>

          {error && (
            <Alert type="error" className="mb-4">
              {error}
            </Alert>
          )}

          {success && (
            <Alert type="success" className="mb-4">
              {success}
            </Alert>
          )}

          <Card>
        <Form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                Event Title
              </label>
              <Input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                placeholder="Enter event title"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Description
              </label>
              <TextArea
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                placeholder="Enter event description"
                rows={4}
              />
            </div>

            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Date
                </label>
                <Input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  required
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Start Time
                  </label>
                  <Input
                    type="time"
                    name="start_time"
                    value={formData.start_time}
                    onChange={handleChange}
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">
                    End Time
                  </label>
                  <Input
                    type="time"
                    name="end_time"
                    value={formData.end_time}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Location
              </label>
              <div className="relative">
                <div className="flex rounded-md shadow-sm">
                  <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                    <MapPinIcon className="h-5 w-5" />
                  </span>
                  <input
                    type="text"
                    id="location"
                    className="flex-1 block w-full rounded-none rounded-r-md border-gray-300 focus:border-sky-blue focus:ring-sky-blue form-input"
                    placeholder="Enter event location"
                    value={formData.location}
                    onChange={handleLocationInputChange}
                    required
                  />
                </div>
                
                {/* Location suggestions dropdown */}
                {showLocationSuggestions && (
                  <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md py-1 text-sm">
                    {locationSuggestions.map((suggestion) => (
                      <div
                        key={suggestion.place_id}
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                        onClick={() => selectLocation(suggestion)}
                      >
                        <p className="font-medium">{suggestion.structured_formatting.main_text}</p>
                        <p className="text-gray-500 text-xs">{suggestion.structured_formatting.secondary_text}</p>
                      </div>
                    ))}
                  </div>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Enter the physical location for the event
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Parish
              </label>
              <Select
                name="parish"
                value={formData.parish}
                onChange={handleParishChange}
                required
                options={BERMUDA_PARISHES.map(parish => ({ value: parish, label: parish }))}
              >
                <option value="">Select a parish</option>
                {BERMUDA_PARISHES.map((parish) => (
                  <option key={parish} value={parish}>
                    {parish}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Event Type
              </label>
              <Select
                name="event_type"
                value={formData.event_type}
                onChange={handleEventTypeChange}
                required
                options={EVENT_TYPES.map(type => ({ value: type, label: type }))}
              >
                <option value="">Select event type</option>
                {EVENT_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Maximum Attendees
              </label>
              <Input
                type="number"
                name="max_attendees"
                value={formData.max_attendees}
                onChange={handleChange}
                required
                min="1"
              />
            </div>

            {/* Image Upload */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Event Image
              </label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                <div className="space-y-1 text-center">
                  {imagePreview ? (
                    <div className="flex flex-col items-center">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="h-32 w-auto object-cover mb-2 rounded"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedImage(null);
                          setImagePreview(null);
                        }}
                        className="text-sm text-red-600 hover:text-red-800"
                      >
                        Remove image
                      </button>
                    </div>
                  ) : (
                    <>
                      <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="flex text-sm text-gray-600">
                        <label
                          htmlFor="file-upload"
                          className="relative cursor-pointer bg-white rounded-md font-medium text-sky-blue hover:text-sky-blue focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-sky-blue"
                        >
                          <span>Upload an image</span>
                          <input
                            id="file-upload"
                            name="file-upload"
                            type="file"
                            className="sr-only"
                            accept="image/*"
                            onChange={handleImageChange}
                          />
                        </label>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-xs text-gray-500">
                        PNG, JPG, GIF up to 5MB
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>

            <Button
              type="submit"
              color="primary"
              disabled={loading || uploadingImage}
            >
              {loading || uploadingImage ? 'Creating Event...' : 'Create Event'}
            </Button>
          </div>
        </Form>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AddEvent;